import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { DeleteOutlined } from '@ant-design/icons'
import Select from '@organisms/Select'
import SecondaryButton from '@organisms/SecondaryButton'
import useCompanies from '@hooks/useCompanies'
import useFilials from '@hooks/useFilials'
import { createInitFilter } from '@utils/filters'
import { DeleteWrapper, RowWrapper, SelectLabel, SelectWrapper } from './styled'

type Props = {
  index: number
  onRemove: () => void
}

const CourseAvailabilityRow: FC<Props> = ({ index, onRemove }) => {
  const { control, setValue } = useFormContext()

  const selectedCompany = useWatch({
    control,
    name: `courseAvailabilities.${index}.company`,
  })

  const selectedFilial = useWatch({
    control,
    name: `courseAvailabilities.${index}.filial`,
  })
  const isInitialCompanyEffect = useRef(true)
  const [isCompanyMenuClosing, setIsCompanyMenuClosing] = useState(false)
  const [isFilialMenuClosing, setIsFilialMenuClosing] = useState(false)

  const companiesResponse = useCompanies()
  const { items: companies = [], length: companiesLength = 0 } =
    companiesResponse.data || {}
  const companiesIsLoading = companiesResponse.isLoading

  useEffect(() => {
    if (
      !companiesIsLoading &&
      companiesLength === 1 &&
      companies.length > 0 &&
      !selectedCompany
    ) {
      setValue(`courseAvailabilities.${index}.company`, companies[0])
      setIsCompanyMenuClosing(true)
    }
  }, [
    companiesIsLoading,
    companiesLength,
    selectedCompany?.value,
    companies,
    index,
    setValue,
  ])

  useEffect(() => {
    if (isInitialCompanyEffect.current) {
      isInitialCompanyEffect.current = false
      return
    }

    setValue(`courseAvailabilities.${index}.filial`, null)
    setIsFilialMenuClosing(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.value])

  const filialsResponse = useFilials({
    skip: !selectedCompany?.id,
    ...(selectedCompany?.id
      ? { initFilters: createInitFilter('company', selectedCompany.id) }
      : {}),
  })
  const { items: filials = [], length: filialsLength = 0 } =
    filialsResponse.data || {}
  const filialsIsLoading = filialsResponse.isLoading || filialsResponse.isFetching

  useEffect(() => {
    if (!isCompanyMenuClosing) return
    setIsCompanyMenuClosing(false)
  }, [isCompanyMenuClosing])

  useEffect(() => {
    if (!isFilialMenuClosing) return
    setIsFilialMenuClosing(false)
  }, [isFilialMenuClosing])

  useEffect(() => {
    if (
      !filialsIsLoading &&
      filialsLength === 1 &&
      filials.length > 0 &&
      selectedCompany &&
      !selectedFilial
    ) {
      setValue(`courseAvailabilities.${index}.filial`, filials[0])
      setIsFilialMenuClosing(true)
    }
  }, [
    selectedCompany?.id,
    filialsIsLoading,
    filialsLength,
    selectedFilial,
    filials,
    index,
    setValue,
  ])

  const filialDisabled = !selectedCompany

  return (
    <RowWrapper>
      <SelectWrapper>
        <SelectLabel>Компания</SelectLabel>
        <Controller
          control={control}
          name={`courseAvailabilities.${index}.company`}
          render={({ field }) => (
            <Select
              {...field}
              options={companies}
              isLoading={companiesIsLoading}
              length={companiesLength}
              setFilters={companiesResponse.setFilters}
              setPage={companiesResponse.setPage}
              placeholder="Компания"
              menuPosition="fixed"
              isDisabled={false}
              // Не прячем выбранную опцию: иначе при автоподстановке (сервер вернул одну
              // подходящую компанию, её сразу выбрали) единственная опция исчезает из
              // меню и рисуется ложная плашка «Нет элементов».
              hideSelectedOptions={false}
              menuIsOpen={isCompanyMenuClosing ? false : undefined}
              isClearable
            />
          )}
        />
      </SelectWrapper>
      <SelectWrapper>
        <SelectLabel>Филиал</SelectLabel>
        <Controller
          control={control}
          name={`courseAvailabilities.${index}.filial`}
          render={({ field }) => (
            <Select
              {...field}
              options={filials}
              isLoading={filialsIsLoading}
              isDisabled={filialDisabled}
              length={filialsLength}
              setFilters={filialsResponse.setFilters}
              setPage={filialsResponse.setPage}
              placeholder="Филиал"
              menuPosition="fixed"
              // См. компанию выше: та же ложная плашка при автоподстановке единственного филиала.
              hideSelectedOptions={false}
              menuIsOpen={isFilialMenuClosing ? false : undefined}
              isClearable
            />
          )}
        />
      </SelectWrapper>
      <DeleteWrapper>
        <SecondaryButton
          type="button"
          onClick={onRemove}
          leftIcon={<DeleteOutlined />}
        >
          Удалить
        </SecondaryButton>
      </DeleteWrapper>
    </RowWrapper>
  )
}

export default CourseAvailabilityRow
